import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, setToken, getToken } from "./api";
import { disconnectSocket } from "./socket";
import type { Staff } from "./types";

interface AuthState {
  staff: Staff | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const STAFF_KEY = "msp.staff";
const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(() => {
    const raw = localStorage.getItem(STAFF_KEY);
    return raw ? (JSON.parse(raw) as Staff) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we hold a staff record but the token is gone, drop it.
    if (staff && !getToken()) setStaff(null);
  }, [staff]);

  const value = useMemo<AuthState>(
    () => ({
      staff,
      loading,
      async login(email, password) {
        setLoading(true);
        try {
          const res = await api<{ token: string; staff: Staff }>(
            "/api/auth/login",
            { method: "POST", body: { email, password } },
          );
          setToken(res.token);
          localStorage.setItem(STAFF_KEY, JSON.stringify(res.staff));
          setStaff(res.staff);
        } finally {
          setLoading(false);
        }
      },
      logout() {
        setToken(null);
        localStorage.removeItem(STAFF_KEY);
        disconnectSocket();
        setStaff(null);
      },
    }),
    [staff, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

const RANK: Record<string, number> = {
  finance: 0,
  l1_support: 1,
  l2_support: 2,
  it_manager: 3,
  super_admin: 4,
};

export function atLeast(role: string | undefined, min: keyof typeof RANK): boolean {
  if (!role) return false;
  return (RANK[role] ?? -1) >= RANK[min];
}
